import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { LOCAL_STORAGE } from '@ng-web-apis/common';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { idleTimeoutMinutes, idleWarningMinutes } from '../config.vars';
import { NotificationService } from '../service/notification.service';
import { AuthService } from './auth.service';

/** The events that count as activity of the user. */
export const ACTIVITY_EVENTS = [
  'keydown',
  'mousedown',
  'mousemove',
  'wheel',
  'touchstart',
  'scroll',
] as const;

/**
 * The last activity in any tab of the app (milliseconds since the epoch), so
 * an active tab keeps the others signed in.
 */
export const LAST_ACTIVITY_KEY = 'hunter.lastActivity';

/** Activity is written to the storage at most this often. */
const STORE_INTERVAL_MS = 5000;

/**
 * Signs the user out after idleTimeoutMinutes without activity and warns
 * idleWarningMinutes before. Activity is keyboard, mouse, touch, scrolling
 * and navigation in the app. The sign-out is the logout of AuthService
 * (Cognito GlobalSignOut); the login then returns to the page.
 *
 * Mouse events arrive often, so they only record the time; one timer checks
 * the time of the last activity when the warning or the end would be due.
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService implements OnDestroy {
  private auth = inject(AuthService);
  private router = inject(Router);
  private zone = inject(NgZone);
  private document = inject(DOCUMENT);
  private storage = inject<Storage>(LOCAL_STORAGE);
  private notificationService = inject(NotificationService);

  private readonly timeoutMs = idleTimeoutMinutes * 60 * 1000;
  private readonly warningMs = idleWarningMinutes * 60 * 1000;

  private watching = false;
  private lastActivity = 0;
  private lastStored = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private warning: { dismiss(): void } | null = null;
  private subscription = new Subscription();

  private readonly onActivity = () => this.recordActivity();

  constructor() {
    this.subscription.add(
      this.auth.isAuthenticated$.subscribe((authenticated) =>
        authenticated ? this.start() : this.stop()
      )
    );
    this.subscription.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => this.recordActivity())
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.stop(false);
  }

  private start() {
    if (this.watching) {
      return;
    }
    this.watching = true;
    // A session that was idle for too long while the app was closed ends
    // right away; otherwise opening the app counts as activity.
    const stored = this.storedActivity();
    if (stored > 0 && Date.now() - stored >= this.timeoutMs) {
      this.lastActivity = stored;
    } else {
      this.recordActivity(true);
    }
    this.zone.runOutsideAngular(() =>
      ACTIVITY_EVENTS.forEach((type) =>
        this.document.addEventListener(type, this.onActivity, {
          capture: true,
          passive: true,
        })
      )
    );
    this.check();
  }

  /** Stops watching; after a sign-out the next sign-in starts afresh. */
  private stop(forget = true) {
    if (!this.watching) {
      return;
    }
    this.watching = false;
    ACTIVITY_EVENTS.forEach((type) =>
      this.document.removeEventListener(type, this.onActivity, {
        capture: true,
      })
    );
    this.clearTimer();
    this.dismissWarning();
    if (forget) {
      try {
        this.storage.removeItem(LAST_ACTIVITY_KEY);
      } catch {
        // No storage: the activity of this tab alone counts.
      }
    }
  }

  private recordActivity(forceStore = false) {
    if (!this.watching) {
      return;
    }
    const now = Date.now();
    this.lastActivity = now;
    if (forceStore || now - this.lastStored >= STORE_INTERVAL_MS) {
      this.lastStored = now;
      try {
        this.storage.setItem(LAST_ACTIVITY_KEY, String(now));
      } catch {
        // No storage: the activity of this tab alone counts.
      }
    }
    if (this.warning) {
      // The user is back: the warning no longer applies.
      this.dismissWarning();
      this.check();
    }
  }

  private storedActivity() {
    try {
      const value = Number(this.storage.getItem(LAST_ACTIVITY_KEY));
      return Number.isFinite(value) ? value : 0;
    } catch {
      return 0;
    }
  }

  private check() {
    this.clearTimer();
    if (!this.watching) {
      return;
    }
    const idle =
      Date.now() - Math.max(this.lastActivity, this.storedActivity());
    if (idle >= this.timeoutMs) {
      this.zone.run(() => this.signOut());
      return;
    }
    const warnAfter = this.timeoutMs - this.warningMs;
    if (idle >= warnAfter) {
      if (!this.warning) {
        this.zone.run(() => {
          this.warning = this.notificationService.notifyDismissible(
            `You will be signed out in ${idleWarningMinutes} minutes because of inactivity. Move the mouse or press a key to stay signed in.`,
            'warning',
            this.timeoutMs - idle
          );
        });
      }
    } else {
      this.dismissWarning();
    }
    const next = idle >= warnAfter ? this.timeoutMs - idle : warnAfter - idle;
    this.zone.runOutsideAngular(() => {
      this.timer = setTimeout(() => this.check(), next);
    });
  }

  private signOut() {
    this.stop();
    this.auth.endSessionAfterInactivity().subscribe();
  }

  private clearTimer() {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private dismissWarning() {
    this.warning?.dismiss();
    this.warning = null;
  }
}
